<template>
  <v-select
    v-model="security"
    class="w-full select"
    :clearable="false"
    :searchable="true"
    :filterable="false"
    label="ticker"
    :options="availableSecurities"
    :disabled="isDisable"
    @search="debouncedFetchSecurities"
    @close="cleanAvailableSecurities"
    ><template #no-options="{ search, searching, loading }">
      <em v-if="isKeywordWithError" style="opacity: 0.5"
        >An error occurred. Please try again later, and contact us if the issue persists.</em
      >
      <em v-else-if="!searching" style="opacity: 0.5">Start typing to search for an instrument.</em>
      <em v-else-if="search.length < 3" style="opacity: 0.5">Provide at least 3 characters</em>
      <em v-else-if="!loading" style="opacity: 0.5"> No results found for {{ search }}.</em>
    </template>
    <template #option="{ name, ticker, currency, start_date, last_date }">
      <div class="flex justify-between">
        <span class="text-md font-bold truncate">{{ name }}</span
        ><span v-if="ticker" class="text-sm"> ({{ ticker }})</span>
      </div>

      <span class="text-sm">Currency: {{ currency }} </span><br /><span class="text-sm">
        First price:
        {{ new Date(start_date).toISOString().slice(0, 10) }}</span
      ><br /><span v-if="last_date" class="text-sm">
        Last available price:
        {{ new Date(last_date).toISOString().slice(0, 10) }}</span
      >
    </template>
    <template #selected-option="{ id, ticker, name, dead, active }">
      <div class="h-full flex items-center">
        <p class="text-md font-bold">
          {{ name }}
        </p>
        <p v-if="ticker" class="pl-2 text-sm">({{ ticker }})</p>
        <div v-if="dead">
          <vs-chip
            v-tooltip="{
              content:
                'This instrument is not available in our database anymore. You won\'t have access to simulation for this portfolio until you remove or replace this instrument.',
              boundariesElement: 'window',
              placement: 'top',
            }"
            class="ml-2"
            color="danger "
            @mouseenter="logTooltip(id)"
            >Removed</vs-chip
          >
        </div>
        <div v-else-if="active === false">
          <vs-chip
            v-tooltip="{
              content:
                'This instrument has been labelled as inactive and won\'t be updated with new prices anymore.',
              boundariesElement: 'window',
              placement: 'top',
            }"
            class="ml-2"
            color="warning"
            @mouseenter="logTooltip(id)"
            >Inactive</vs-chip
          >
        </div>
      </div>
    </template>
    <template #open-indicator="{}"> </template>
  </v-select>
</template>

<script>
import _ from "lodash";
import vSelect from "vue-select";

import {
  SECURITY_SEARCH_EVENT,
  STATUS_FAILURE,
  STATUS_SUCCESS,
  TOOLTIP_DISPLAYED_EVENT,
  UNDERLYING_SELECTION_EVENT,
} from "@/assets/constants/analytics";
import axios from "@/http/axios";

export default {
  name: "SpGlobalInputSearch",
  components: {
    "v-select": vSelect,
  },
  props: {
    // v-model prop
    value: {
      type: Object,
    },
    isDisable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      security: this.value,
      availableSecurities: this.value ? [this.value] : [],
      attributes: {},
      isKeywordWithError: false,
      lastQueryTimestamp: null,
    };
  },
  methods: {
    debouncedFetchSecurities: _.debounce(function (search, loading) {
      this.isKeywordWithError = false;
      if (search && search.length >= 3) {
        loading(true);
        this.fetchSecurities(search, loading);
      }
    }, 500),
    fetchSecurities(search, loading) {
      // To avoid the case where two successive research are made through
      // the search bar, and the first one is slower than the second one,
      // resulting in the first result to be displayed while the second
      // keywords are shown. Imagine typing 'qqq', then directly 'spy' before
      // the result appears and finally getting the result for 'qqq' overwriting
      // the result for 'spy'.
      // What we will do is simply have a global identifier, which will be a
      // timestamp we update every time a query is made, hence this identifier
      // will always have the value of the timestamp of the last query + we store
      // for the current query its timestamp. When the result arrives, if the
      // current timestamp is different than the last global timestamp, it means
      // we were too slow and we discard the result.
      const now = new Date();
      this.lastQueryTimestamp = now.getTime();
      const currentQueryTimestamp = now.getTime();
      axios
        .get(`/api/help/search-securities-by-text`, {
          params: {
            keyword: search,
          },
        })
        .then((response) => {
          // The moment we check if the search is still valid compared
          // to what is the current keywords in the search bar.
          if (this.lastQueryTimestamp != currentQueryTimestamp) {
            return;
          }

          // Put the object key in lower case.
          this.availableSecurities = response.data.map((x) =>
            Object.keys(x).reduce((acc, key) => {
              acc[key.toLowerCase()] = x[key];
              return acc;
            }, {})
          );
          this.$analytics.logEvent(SECURITY_SEARCH_EVENT, {
            query: search,
            message: `Found ${this.availableSecurities.length} securities`,
            status: STATUS_SUCCESS,
          });
          loading(false);
        })
        .catch((err) => {
          loading(false);
          this.isKeywordWithError = true;
          this.$analytics.logEvent(SECURITY_SEARCH_EVENT, {
            query: search,
            message: err.message || err,
            status: STATUS_FAILURE,
          });

          // We rethrow the error for sentry to catch it.
          throw err;
        });
    },
    cleanAvailableSecurities() {
      if (this.security) {
        this.$analytics.logEvent(UNDERLYING_SELECTION_EVENT, {
          underlying: this.security.id,
        });
        this.availableSecurities = [this.security];
        // The emit input for v-model use.
        this.$emit("input", this.security);
      }
    },
    logTooltip(id) {
      this.$analytics.logEvent(TOOLTIP_DISPLAYED_EVENT, {
        tooltip: `security_${id}`,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.con-vs-chip {
  margin-bottom: 0px;
  min-height: 0px;
}
</style>
